/**
 * ============================================================================
 * FILE: useMetricsPolling.js
 * Location: src/pages/AIControlRoomAdaptive/hooks/useMetricsPolling.js
 * ============================================================================
 *
 * Hook for fetching Control Room metrics from the pre-computed materialized view.
 * - Polls every 30 seconds (MV refreshes every 60s server-side)
 * - Supports manual refresh with rate limiting
 * - Returns data in exact shape expected by AIControlRoomAdaptive component
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../../../lib/supabaseClient';
import { PLAN_PRICE } from '../../../lib/planPricing';

/**
 * Fetch platform settings for business hours calculation
 */
async function fetchPlatformSettings(tenantId) {
  if (!tenantId || tenantId === 'all') return null;

  const { data, error } = await supabase
    .from('platform_settings')
    .select('key, value')
    .eq('tenant_id', tenantId)
    .in('key', ['officeOpenHour', 'officeCloseHour', 'officeDays', 'avg_deal_value']);

  if (error) {
    console.error('[useMetricsPolling] Error fetching platform settings:', error);
    return null;
  }

  const settings = {};
  data?.forEach(row => {
    settings[row.key] = row.value;
  });

  return settings;
}

/**
 * Convert raw hours to readable time display
 */
function formatBusinessTime(rawHours, settings) {
  if (!rawHours || rawHours === 0) return '0h';

  // Convert to calendar days
  const days = rawHours / 24;

  if (days < 1) {
    // Whole hours hid the real figure (0.6h and 1.4h both read "1h", and 20 minutes read "0h"), so a +67% trend
    // sat beside an unchanged "1h". Under an hour show minutes; otherwise hours plus minutes.
    const totalMin = Math.round(rawHours * 60);
    if (totalMin < 60) return `${Math.max(totalMin, 1)}m`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  } else if (days < 7) {
    return `${days.toFixed(1)}d`;
  } else {
    return `${Math.round(days)}d`;
  }
}

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com';
};

/**
 * What the tenant is ACTUALLY billed per month, from Stripe.
 *
 * The browser cannot hold a Stripe secret, so this goes through
 * GET /billing/billed-price, which resolves the live subscription and applies any
 * active coupon. Confirmed 2026-09-06: The Da Silva Team's list price is $2,497
 * but coupon "DaSilva_Coupon" bills them $1,997 until 2027-01-23, and no Supabase
 * column records that.
 *
 * Returns null on any failure. The caller then falls back to the older, weaker
 * signals — this is deliberately additive so a Stripe outage degrades the card to
 * what it showed yesterday rather than blanking it.
 */
async function fetchStripeBilledPrice(tenantId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const apiBase = getApiBaseUrl();
    const root = apiBase.includes('/api') ? apiBase : `${apiBase}/api`;
    const res = await fetch(`${root}/billing/billed-price?tenant_id=${encodeURIComponent(tenantId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) return null;

    const body = await res.json();
    if (body?.billedPrice == null) {
      console.warn('[useMetricsPolling] No Stripe billed price:', body?.unavailableReason);
      return null;
    }
    return Number(body.billedPrice);
  } catch (err) {
    console.warn('[useMetricsPolling] billed-price lookup failed:', err?.message);
    return null;
  }
}

/**
 * Fetch tenant pricing for cost calculation.
 *
 * Three sources, strongest first:
 *   1. stripeBilledPrice   what Stripe actually bills, coupons applied
 *   2. plan_limits.monthly_price   a hand-entered negotiated price (only ever set
 *      on one tenant, and NOT set on the two custom-pricing tenants that have a
 *      real deal, so it cannot be relied on alone)
 *   3. PLAN_PRICE[plan]   list price for the tier
 */
async function fetchTenantPricing(tenantId) {
  if (!tenantId || tenantId === 'all') return null;

  const [tenantRes, stripeBilledPrice] = await Promise.all([
    supabase.from('tenants').select('plan, is_custom_pricing, plan_limits').eq('id', tenantId).single(),
    fetchStripeBilledPrice(tenantId),
  ]);

  const { data, error } = tenantRes;
  if (error) {
    console.error('[useMetricsPolling] Error fetching tenant pricing:', error);
    return stripeBilledPrice === null ? null : { plan: null, isCustomPricing: false, monthlyPrice: null, stripeBilledPrice };
  }

  return {
    plan: data?.plan?.toLowerCase(),
    isCustomPricing: data?.is_custom_pricing === true,
    monthlyPrice: Number(data?.plan_limits?.monthly_price) || null,
    stripeBilledPrice,
  };
}

/**
 * Calculate cost per hot lead (prorated by period).
 *
 * Stripe's billed price wins whenever we have it — it is the only source that
 * knows about coupons. The two weaker sources below it are kept as a fallback so
 * a Stripe hiccup degrades this card instead of blanking it.
 */
function calculateCostPerHotLead(pricing, hotLeadsCount, dateRange) {
  if (!hotLeadsCount || hotLeadsCount === 0) return 0;

  const monthlyCost = pricing?.stripeBilledPrice != null
    ? pricing.stripeBilledPrice
    : (pricing?.isCustomPricing && pricing.monthlyPrice
      ? pricing.monthlyPrice
      : (PLAN_PRICE[pricing?.plan] || PLAN_PRICE.starter));

  // Prorate cost based on date range
  const periodDays = {
    today: 1,
    yesterday: 1,
    last7: 7,
    last30: 30,
    thisMonth: new Date().getDate(),
    lastMonth: 30,
    allTime: 30
  };

  const days = periodDays[dateRange] || 30;
  const proratedCost = (monthlyCost * days) / 30;

  return Math.round(proratedCost / hotLeadsCount);
}

const POLL_INTERVAL = 30000; // 30 seconds
const MANUAL_REFRESH_COOLDOWN = 5000; // 5 seconds between manual refreshes

/**
 * Default metrics shape - prevents undefined errors on first render
 */
const DEFAULT_METRICS = {
  responseRate: 0,
  responseTrend: 0,
  avgTimeToHot: 0,
  timeToHotTrend: 0,
  messageVolume: 0,
  volumeTrend: 0,
  hotLeadRate: 0,
  hotLeadTrend: 0,
  pipelineAdded: 0,
  pipelineTrend: 0,
  pipelineHotLeads: 0,
  prevPipelineHotLeads: 0,
  prevHotLeads: 0,
  aiAccuracy: 0,
  optOutRate: 0,
  optOutTrend: 0,
  costPerHotLead: 0,
  costTrend: 0,
  salesResponseTime: 0,
  salesResponseTrend: 0,
  identityCaptureTrend: 0,
  sessionDurationTrend: 0,
  hotLeadsAwaiting: 0,
  stuckLeads: 0,
  totalHotLeads: 0,
  totalLeads: 0,
  avgTimeToHotDisplay: '0h',
  computedAt: null,
  // CommandBar real-time metrics
  activeConversations: 0,
  hotLeads: 0,
  bookedAppointments: 0,
  qualRate: 0
};

/**
 * Demo metrics - impressive fake data for demo tenants
 */
const DEMO_METRICS = {
  responseRate: 68,
  responseTrend: 12,
  avgTimeToHot: 4.2,
  timeToHotTrend: -15,
  messageVolume: 2847,
  volumeTrend: 23,
  hotLeadRate: 34,
  hotLeadTrend: 8,
  pipelineAdded: 127500,
  pipelineTrend: 18,
  pipelineHotLeads: 17,
  prevPipelineHotLeads: 14,
  prevHotLeads: 36,
  aiAccuracy: 91,
  optOutRate: 2.1,
  optOutTrend: -0.3,
  costPerHotLead: 14,
  costTrend: -9,
  salesResponseTime: 8,
  salesResponseTrend: -12,
  identityCaptureTrend: 5,
  sessionDurationTrend: 7,
  hotLeadsAwaiting: 7,
  stuckLeads: 2,
  totalHotLeads: 43,
  totalLeads: 847,
  avgTimeToHotDisplay: '4.2h',
  computedAt: new Date().toISOString(),
  // CommandBar real-time metrics
  activeConversations: 23,
  hotLeads: 7,
  bookedAppointments: 12,
  qualRate: 78
};

/**
 * Check if tenant is a demo tenant
 */
async function checkIsDemo(tenantId) {
  if (!tenantId || tenantId === 'all') return false;

  const { data, error } = await supabase
    .from('tenants')
    .select('is_demo')
    .eq('id', tenantId)
    .single();

  if (error) {
    console.error('[useMetricsPolling] Error checking demo status:', error);
    return false;
  }

  return data?.is_demo === true;
}

/**
 * Fetches control room metrics from the materialized view via RPC
 */
async function fetchRpcMetrics({ tenantId, campaignId, dateRange, channel }) {
  const effectiveTenantId = tenantId === 'all' ? null : tenantId;
  const effectiveCampaignId = campaignId === 'all' ? null : campaignId;

  // Web channel uses a dedicated RPC that returns web-specific metrics
  const rpcName = channel === 'web' ? 'get_web_chat_metrics' : 'get_control_room_metrics';

  const { data, error } = await supabase.rpc(rpcName, {
    p_tenant_id: effectiveTenantId,
    p_campaign_id: effectiveCampaignId,
    p_date_range: dateRange
  });

  if (error) {
    // Division by zero in trend calculations — return defaults with trends zeroed
    if (error.message?.includes('division by zero')) {
      console.warn(`[useMetricsPolling] Division by zero in ${rpcName} for date range "${dateRange}" — returning safe defaults`);
      return null;
    }
    // Postgres statement timeout (57014). The RPC is heavy under load; a single
    // slow poll should NOT replace the dashboard with a scary error — the next
    // poll (30s) almost always succeeds. Tag it so the caller keeps last-good data.
    const isTimeout = error.code === '57014'
      || /statement timeout|canceling statement/i.test(error.message || '');
    throw Object.assign(new Error(error.message), { isTimeout });
  }

  return data;
}


/**
 * Hook for polling Control Room metrics
 *
 * @param {Object} options
 * @param {string} options.tenantId - Tenant UUID or 'all' for partner/global admins
 * @param {string} options.campaignId - Campaign UUID or 'all' for all campaigns
 * @param {string} options.dateRange - One of: 'today', 'last7', 'last30', 'last90'
 * @param {boolean} options.enabled - Whether polling should be active (default: true)
 *
 * @returns {Object} { data, isLoading, error, lastUpdated, refresh, canRefresh }
 */
export function useMetricsPolling({
  tenantId,
  campaignId = 'all',
  dateRange = 'last7',
  channel = 'sms',
  enabled = true
} = {}) {
  const [data, setData] = useState(DEFAULT_METRICS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [canRefresh, setCanRefresh] = useState(true);

  const pollIntervalRef = useRef(null);
  const refreshCooldownRef = useRef(null);

  /**
   * Core fetch function
   */
  const loadMetrics = useCallback(async (showLoading = false) => {
    if (!tenantId) {
      setError(new Error('Tenant ID is required'));
      setIsLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setIsLoading(true);
      }

      // Check demo first to avoid unnecessary parallel queries
      const isDemo = await checkIsDemo(tenantId);
      if (isDemo) {
        console.log('[useMetricsPolling] Demo tenant detected - returning demo metrics');
        setData(DEMO_METRICS);
        setLastUpdated(new Date());
        setError(null);
        return;
      }

      // Fetch metrics, platform settings, and tenant pricing in parallel
      const [rpcMetrics, platformSettings, tenantPricing] = await Promise.all([
        fetchRpcMetrics({ tenantId, campaignId, dateRange, channel }),
        fetchPlatformSettings(tenantId),
        fetchTenantPricing(tenantId)
      ]);

      // Add formatted business time, calculated cost, and tenant-driven pipeline value.
      // Cost is derived client-side (plan price / hot leads), so its trend is computed
      // here from the RPC's prevHotLeads. Same proration for both periods → cost ∝ 1/hot,
      // so the trend reflects the real efficiency change. Lower cost is better (FE owns arrow).
      const avgDealValue = Number(platformSettings?.avg_deal_value) || 0;
      // Pipeline values BOOKED appointments only, never the raw hot count. Every handoff is
      // a hot lead now (info request, callback request, referral), and pricing a "text me a
      // brochure" at a full deal's value is how this card got untrustworthy in the first
      // place. pipelineHotLeads = hot AND booking_completed, from the RPC.
      const bookedHot = rpcMetrics?.pipelineHotLeads || 0;
      const curCost = calculateCostPerHotLead(tenantPricing, rpcMetrics?.totalHotLeads, dateRange);
      const prevCost = calculateCostPerHotLead(tenantPricing, rpcMetrics?.prevHotLeads, dateRange);
      const costTrend = prevCost > 0
        ? Math.round(((curCost - prevCost) / prevCost) * 1000) / 10
        : 0;
      const enhancedMetrics = {
        ...(rpcMetrics || DEFAULT_METRICS),
        avgTimeToHotDisplay: formatBusinessTime(rpcMetrics?.avgTimeToHot, platformSettings),
        costPerHotLead: curCost,
        costTrend,
        pipelineAdded: bookedHot * avgDealValue
      };

      setData(enhancedMetrics);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      // Transient statement timeout — skip this poll, keep the last-good data on
      // screen rather than flipping the whole dashboard to an error state.
      if (err?.isTimeout) {
        console.warn('[useMetricsPolling] Metrics query timed out — keeping last data, will retry next poll');
        return;
      }
      console.error('[useMetricsPolling] Error fetching metrics:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, campaignId, dateRange, channel]);

  /**
   * Manual refresh with cooldown
   */
  const refresh = useCallback(() => {
    if (!canRefresh) return;

    // Disable refresh button
    setCanRefresh(false);

    // Fetch fresh data
    loadMetrics(false);

    // Re-enable after cooldown
    refreshCooldownRef.current = setTimeout(() => {
      setCanRefresh(true);
    }, MANUAL_REFRESH_COOLDOWN);
  }, [canRefresh, loadMetrics]);

  /**
   * Initial load and polling setup.
   * Re-runs whenever loadMetrics changes — which happens when tenantId, campaignId,
   * or dateRange change (they are in loadMetrics' useCallback deps). This means
   * filter changes automatically trigger a fresh fetch + restart the polling interval.
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initial fetch
    loadMetrics(true);

    // Set up polling
    pollIntervalRef.current = setInterval(() => {
      loadMetrics(false);
    }, POLL_INTERVAL);

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (refreshCooldownRef.current) {
        clearTimeout(refreshCooldownRef.current);
      }
    };
  }, [enabled, loadMetrics]);

  return {
    data,
    isLoading,
    error,
    lastUpdated,
    refresh,
    canRefresh
  };
}

export default useMetricsPolling;
